import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ShipmentDetails from '../components/ShipmentDetails';
import './PackingListManager.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export default function PackingListManager({ token, user }) {
  const navigate = useNavigate();

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [expandedPlId, setExpandedPlId] = useState(null);

  useEffect(() => {
    if (token) {
      fetchPackingLists();
    }
  }, [statusFilter, token]);

  const fetchPackingLists = async () => {
    try {
      setLoading(true);
      let url = `${API_URL}/packing-lists?grouped=true`;
      if (statusFilter) {
        url += `&status=${statusFilter}`;
      }

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        setError('Failed to load packing lists');
        return;
      }

      const data = await response.json();
      setGroups(Array.isArray(data.groups) ? data.groups : []);
    } catch (err) {
      setError(`Error loading packing lists: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (packingList) => {
    setEditingId(packingList.packing_list_id);
    setEditForm({
      order_number: packingList.order_number || '',
      invoice_number: packingList.invoice_number || '',
      client_po_number: packingList.client_po_number || '',
      notes: packingList.notes || '',
    });
    setError('');
    setSuccess('');
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveEdit = async () => {
    try {
      const response = await fetch(`${API_URL}/packing-lists/${editingId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(editForm),
      });

      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Failed to update packing list');
        return;
      }

      setSuccess('Packing list updated successfully');
      setEditingId(null);
      setTimeout(() => fetchPackingLists(), 1000);
    } catch (err) {
      setError(`Error updating packing list: ${err.message}`);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setError('');
    setSuccess('');
  };

  const handleDelete = async (packingListId) => {
    if (!window.confirm('Are you sure you want to delete this packing list?')) return;

    try {
      const response = await fetch(`${API_URL}/packing-lists/${packingListId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Failed to delete packing list');
        return;
      }

      setSuccess('Packing list deleted successfully');
      setTimeout(() => fetchPackingLists(), 1000);
    } catch (err) {
      setError(`Error deleting packing list: ${err.message}`);
    }
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      pending_data: 'badge-warning',
      complete: 'badge-success',
      delivered: 'badge-info',
      partial: 'badge-primary',
    };
    return classes[status] || 'badge-secondary';
  };

  const getPackingListForDetails = (group) => {
    const packingLists = group.packing_lists || [];
    if (packingLists.length === 1) {
      return packingLists[0];
    }
    if (expandedPlId) {
      return packingLists.find((pl) => pl.packing_list_id === expandedPlId) || null;
    }
    return null;
  };

  if (loading) return <div className="loading">Loading packing lists...</div>;

  const totalPackingLists = groups.reduce((sum, g) => sum + (g.packing_lists?.length || 0), 0);

  return (
    <div className="packing-list-manager-container">
      <div className="manager-header">
        <h1>Packing Lists</h1>
        <p>Grouped by Shesha Reference — {groups.length} shipment(s), {totalPackingLists} packing list(s)</p>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="filters">
        <label htmlFor="statusFilter">Filter by Status:</label>
        <select
          id="statusFilter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All Statuses</option>
          <option value="pending_data">Pending Data</option>
          <option value="complete">Complete</option>
          <option value="delivered">Delivered</option>
        </select>
      </div>

      {groups.length === 0 ? (
        <div className="no-data">No packing lists found</div>
      ) : (
        <div className="shipment-groups">
          {groups.map((group) => {
            const groupKey = group.shesha_reference || `group-${group.shipment?.shipment_id}`;
            const isGroupExpanded = expandedGroup === groupKey;

            return (
              <div key={groupKey} className="shipment-group-card">
                <div
                  className="shipment-group-header"
                  onClick={() => setExpandedGroup(isGroupExpanded ? null : groupKey)}
                >
                  <div>
                    <h2>{group.shesha_reference || 'No Shesha Reference'}</h2>
                    <p className="group-meta">
                      {group.shipment?.client_name && <span>Client: {group.shipment.client_name}</span>}
                      {group.shipment?.sfr_number && <span>Client Ref/PO: {group.shipment.sfr_number}</span>}
                      <span>{group.packing_lists?.length || 0} packing list(s)</span>
                    </p>
                  </div>
                  <span className="expand-btn">{isGroupExpanded ? '▼' : '▶'}</span>
                </div>

                {isGroupExpanded && (
                  <div className="shipment-group-body">
                    {group.shipment && (
                      <ShipmentDetails
                        shipment={group.shipment}
                        packing_list={getPackingListForDetails(group)}
                        loading={false}
                        canEdit={user?.role === 'admin'}
                        token={token}
                        onUpdated={() => fetchPackingLists()}
                      />
                    )}

                    <div className="packing-lists-grid">
                      {(group.packing_lists || []).map((pl) => (
                        <div key={pl.packing_list_id} className="packing-list-card">
                          <div className="card-header">
                            <div className="header-content">
                              <h3>{pl.packing_list_reference || `${group.shesha_reference}_${pl.invoice_number}`}</h3>
                              <div className="card-badges">
                                <span className={`badge ${getStatusBadgeClass(pl.status)}`}>{pl.status}</span>
                                {pl.pallet_mode === 'mixed' && (
                                  <span className="badge badge-mixed">Mixed pallets</span>
                                )}
                              </div>
                            </div>
                            <button
                              type="button"
                              className="expand-btn"
                              onClick={() =>
                                setExpandedPlId(
                                  expandedPlId === pl.packing_list_id ? null : pl.packing_list_id
                                )
                              }
                            >
                              {expandedPlId === pl.packing_list_id ? '▼' : '▶'}
                            </button>
                          </div>

                          <div className="card-content">
                            <div className="quick-info">
                              {pl.supplier && (
                                <div className="info-item">
                                  <span className="label">Supplier:</span>
                                  <span className="value">{pl.supplier.supplier_name}</span>
                                </div>
                              )}
                              <div className="info-item">
                                <span className="label">Invoice:</span>
                                <span className="value">{pl.invoice_number || 'N/A'}</span>
                              </div>
                              <div className="info-item">
                                <span className="label">Upload Date:</span>
                                <span className="value">
                                  {pl.upload_date ? new Date(pl.upload_date).toLocaleDateString() : 'N/A'}
                                </span>
                              </div>
                            </div>

                            {expandedPlId === pl.packing_list_id && (
                              <div className="expanded-section">
                                {editingId === pl.packing_list_id ? (
                                  <div className="edit-form-section">
                                    <h4>Edit Details</h4>
                                    <div className="form-group">
                                      <label>Shesha Note</label>
                                      <input
                                        type="text"
                                        name="order_number"
                                        value={editForm.order_number}
                                        onChange={handleEditChange}
                                        placeholder="Internal info only"
                                      />
                                    </div>
                                    <div className="form-group">
                                      <label>Invoice Number</label>
                                      <input
                                        type="text"
                                        name="invoice_number"
                                        value={editForm.invoice_number}
                                        onChange={handleEditChange}
                                      />
                                    </div>
                                    <div className="form-group">
                                      <label>Supplier Ref/PO</label>
                                      <input
                                        type="text"
                                        name="client_po_number"
                                        value={editForm.client_po_number}
                                        onChange={handleEditChange}
                                      />
                                    </div>
                                    <div className="form-group">
                                      <label>Notes</label>
                                      <textarea
                                        name="notes"
                                        value={editForm.notes}
                                        onChange={handleEditChange}
                                        rows="3"
                                      />
                                    </div>
                                    <div className="form-buttons">
                                      <button type="button" className="btn btn-primary" onClick={handleSaveEdit}>
                                        Save
                                      </button>
                                      <button type="button" className="btn btn-secondary" onClick={handleCancelEdit}>
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="details-section">
                                    <div className="detail-row">
                                      <span className="label">Shesha Note:</span>
                                      <span className="value">{pl.order_number || 'N/A'}</span>
                                    </div>
                                    <div className="detail-row">
                                      <span className="label">Invoice Number:</span>
                                      <span className="value">{pl.invoice_number || 'N/A'}</span>
                                    </div>
                                    <div className="detail-row">
                                      <span className="label">Supplier Ref/PO:</span>
                                      <span className="value">{pl.client_po_number || 'N/A'}</span>
                                    </div>
                                    <div className="detail-row">
                                      <span className="label">Notes:</span>
                                      <span className="value">{pl.notes || 'None'}</span>
                                    </div>
                                  </div>
                                )}

                                <div className="action-buttons">
                                  {editingId !== pl.packing_list_id && (
                                    <>
                                      <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => navigate(`/packing-list/${pl.packing_list_id}/cargo`)}
                                      >
                                        Add/Edit Cargo
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={() => handleEdit(pl)}
                                      >
                                        Edit
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-danger"
                                        onClick={() => handleDelete(pl.packing_list_id)}
                                      >
                                        Delete
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
